import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { analyticsApi } from '../../api/analytics';
import { workspacesApi, getWorkspaceId, type WorkspaceInfo } from '../../api/workspaces';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useDrillStack } from '../../hooks/useDrillStack';
import { paramsToView, viewToParams } from '../../components/analytics/drillView';
import ViewRenderer from '../../components/analytics/ViewRenderer';
import SyncSplash from '../../components/analytics/SyncSplash';

/**
 * Analytics dashboard — the drill-down surface (deterministic, egress-free).
 * L0 overview → clickable tiles/aging/problems → customer → invoice →
 * possible matching payments. Every click is deterministic (no LLM in the
 * navigation path); Mistral only powers the NL search box and "Paaiškinti".
 * View rendering lives in ViewRenderer (shared with the /analytics/ask chat
 * cards); this page owns the URL, breadcrumbs and the NL entry point.
 */
export default function AnalyticsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enableAnalytics, loading: flagsLoading } = useFeatureFlags();
  const workspaceId = getWorkspaceId();

  // The inbox is ALWAYS the drill root — when arriving from the portfolio with
  // ?view=X, the stack starts as [inbox, X] so breadcrumbs + back always exist.
  // consumeBrowserBack (finding 35): the browser's Back unwinds the drill
  // stack like the „← Atgal" button instead of leaving the page.
  const initialView = paramsToView(Object.fromEntries(searchParams.entries()));
  const { stack, view, push, popTo, reset } = useDrillStack(
    initialView.kind === 'inbox' ? [initialView] : [{ kind: 'inbox' }, initialView],
    { consumeBrowserBack: true }
  );

  // Keep the URL shareable: reflect the current drill view (replace, no history spam)
  useEffect(() => {
    setSearchParams(viewToParams(view), { replace: true });
  }, [view, setSearchParams]);

  const { isLoading: overviewLoading, error: overviewError } = useQuery({
    queryKey: ['analytics-overview', workspaceId],
    queryFn: analyticsApi.overview,
    enabled: !!workspaceId,
    staleTime: 60_000,
  });

  const { data: workspaces } = useQuery({
    queryKey: ['workspaces'],
    queryFn: workspacesApi.list,
  });
  const activeWorkspace: WorkspaceInfo | undefined = workspaces?.find(w => w.isActive);
  const dataAsOf = activeWorkspace?.lastSyncedAtUtc ?? null;
  // Hosted workspace whose first sync hasn't landed → splash (never wrong numbers).
  // Poll while in that state so the splash clears when the sync completes.
  const awaitingFirstSync = activeWorkspace?.sourceKind === 'mysql' && !dataAsOf;
  useQuery({
    queryKey: ['workspaces', 'sync-poll'],
    queryFn: workspacesApi.list,
    refetchInterval: awaitingFirstSync ? 15_000 : false,
  });

  // Per-workspace AI consent: NL search + explain render only when egress is allowed
  const { data: aiStatus } = useQuery({
    queryKey: ['ai-status', workspaceId],
    queryFn: () => analyticsApi.aiStatus(workspaceId!),
    enabled: !!workspaceId,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const aiEnabled = !!aiStatus?.aiEnabled;

  if (!flagsLoading && !enableAnalytics) {
    return <NavigateHome />;
  }

  return (
    <div className="w-full">
      {/* Header: title, data-as-of, NL search (AI-gated), ask-chat cross-link */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-xl font-semibold text-gray-900">{t('analytics.title')}</h1>
        <span className="text-xs text-gray-400">
          {dataAsOf ? t('analytics.dataAsOf', { time: new Date(dataAsOf).toLocaleString('lt-LT') }) : ''}
        </span>
        <div className="flex-1" />
      </div>

      {/* Back + breadcrumbs (drill path) — the way BACK is a real button, not a
          subtle crumb: every drill from a card must be obviously reversible. */}
      {stack.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 p-2 bg-white border border-gray-200 rounded-lg">
          <button
            onClick={() => popTo(stack.length - 2)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-lg hover:border-gray-500"
            title={stack.length >= 2 ? t('analytics.view.' + stack[stack.length - 2]!.kind) : undefined}
          >
            ← {t('analytics.back')}
          </button>
          <span className="text-gray-300">|</span>
          {stack.map((item, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <span className="text-gray-300">›</span>}
              <button
                onClick={() => popTo(i)}
                className={`px-2 py-0.5 rounded hover:bg-gray-100 ${i === stack.length - 1 ? 'font-medium text-gray-900' : 'text-gray-500'}`}
              >
                {t(`analytics.view.${item.kind}`)}
                {item.kind === 'customer' && item.personName ? `: ${item.personName}` : ''}
              </button>
            </span>
          ))}
          <button onClick={() => reset()} className="ml-auto text-xs text-gray-400 hover:text-gray-600 underline">
            {t('common.toStart')}
          </button>
        </div>
      )}

      {awaitingFirstSync ? (
        <SyncSplash />
      ) : (
        <ViewRenderer
          view={view}
          onDrill={push}
          aiEnabled={aiEnabled}
          explain
          overviewLoading={overviewLoading}
          overviewError={overviewError instanceof Error ? overviewError.message : ''}
          onOpenBanking={() => navigate('/banking')}
          onOpenVat={() => navigate('/reports/vat')}
        />
      )}
    </div>
  );
}

function NavigateHome() {
  const navigate = useNavigate();
  useEffect(() => { navigate('/', { replace: true }); }, [navigate]);
  return null;
}
